// The decision tree. A lean, conditional, one-tap flow — the minimum set of
// questions needed to determine eligibility, each revealing the next only when
// it matters. Question ids stay identical to production (from 1-eligibility.json
// + the care profile) so a future mapping is trivial; the prototype additions
// are prefixed `proto-`. Every answer carries a subject (brief §6.4).
//
// The quiz asks quick BANDS (age band, savings band, income band) rather than
// exact dates and amounts — that's the whole point of the reveal-fast approach.
// The precise figures are gathered later, in the collection step, when we
// actually fill a form.

import type {
  AwardWithParts,
  ConditionDuration,
  DisabilityType,
  ExistingAward,
  NeedsHelp,
  ScreenerInput,
} from '@/benefits-flow/engine/metrics';
import type { Nation, Subject } from '@/benefits-flow/engine/types';

// Who the care is for. Set at the start of the quiz (the triage step) and
// substituted into every `{name}`, so nothing ever reads "where is Mum based"
// without first asking who Mum is. Names are chosen to fit the prompt grammar.
export type Recipient = { id: string; label: string; name: string };

export const RECIPIENTS: Recipient[] = [
  { id: 'mum', label: 'My mum', name: 'Mum' },
  { id: 'dad', label: 'My dad', name: 'Dad' },
  { id: 'partner', label: 'My partner', name: 'your partner' },
  {
    id: 'someone-else',
    label: 'Someone else',
    name: 'the person you care for',
  },
];

export type AnswerValue = string | number | boolean | string[];
export type Answers = Record<string, AnswerValue>;

// Every quiz question is a tap: a single choice, a yes/no, or a short
// multi-select. No free text or number pads in the quiz itself.
export type QuestionType = 'choice' | 'boolean' | 'multi-select';

export type Option = {
  id: string;
  label: string;
  value?: string;
  /** Only offered in these nations. Absent means everywhere. */
  nations?: Nation[];
};

export type Question = {
  id: string;
  subject: Subject;
  prompt: string;
  help?: string;
  type: QuestionType;
  options?: Option[];
  // Only shown when this predicate passes over the input built so far.
  when?: (input: ScreenerInput) => boolean;
  // Human-readable version of `when`, for the tree view.
  gate?: string;
  // Fold a raw answer into the running ScreenerInput.
  apply: (input: ScreenerInput, value: AnswerValue) => void;
  // A short line describing what answering this can unlock, for the tree view.
  unlocks?: string;
};

const optionValue = (q: Question, id: string): string =>
  q.options?.find((o) => o.id === id)?.value ?? id;

// The options this person is offered. With no nation yet, everything.
export const optionsFor = (q: Question, nation?: Nation): Option[] =>
  (q.options ?? []).filter(
    (o) => !o.nations || nation === undefined || o.nations.includes(nation),
  );

// ── Questions ──

const LOCATION: Question = {
  id: 'care-recipient-location',
  subject: 'care-recipient',
  prompt: 'Where does {name} live?',
  help: 'Care rules differ by nation, so this shapes everything after it.',
  type: 'choice',
  unlocks: 'Splits England / Scotland / Wales / NI rules',
  options: [
    {
      id: 'care-recipient-location-england',
      label: 'England',
      value: 'england',
    },
    {
      id: 'care-recipient-location-scotland',
      label: 'Scotland',
      value: 'scotland',
    },
    { id: 'care-recipient-location-wales', label: 'Wales', value: 'wales' },
    {
      id: 'care-recipient-location-northern-ireland',
      label: 'Northern Ireland',
      value: 'northern-ireland',
    },
    {
      id: 'care-recipient-location-other',
      label: 'Outside the UK',
      value: 'other',
    },
  ],
  apply: (input, v) => {
    input.nation = optionValue(LOCATION, v as string) as Nation;
  },
};

// Age as a band, not a date — the quiz only needs which side of 18, 60 and 66
// they're on. 18 rules out Universal Credit; 60 is the bus pass age outside
// England. The exact DOB is collected later, in the form. The state pension age
// is moving to 67 and a band cannot place anyone against that, which is
// accepted rather than modelled.
const AGE_BAND: Question = {
  id: 'care-recipient-date-of-birth',
  subject: 'care-recipient',
  prompt: 'How old is {name}?',
  type: 'choice',
  unlocks: 'Attendance Allowance, Pension Credit, bus pass',
  options: [
    { id: 'age-under-18', label: 'Under 18', value: '16' },
    { id: 'age-18-59', label: '18 to 59', value: '40' },
    { id: 'age-60-65', label: '60 to 65', value: '62' },
    { id: 'age-66-plus', label: '66 or over', value: '70' },
  ],
  apply: (input, v) => {
    input.ageYears = Number(optionValue(AGE_BAND, v as string));
  },
};

const NEEDS_HELP: Question = {
  id: 'care-recipient-needs-help-time-of-day',
  subject: 'care-recipient',
  prompt: 'Does {name} need help from someone through the day or night?',
  type: 'choice',
  unlocks: 'Attendance Allowance, social care',
  options: [
    { id: 'care-recipient-needs-help-none', label: 'No', value: 'none' },
    {
      id: 'care-recipient-needs-help-during-day',
      label: 'In the day',
      value: 'day',
    },
    {
      id: 'care-recipient-needs-help-during-night',
      label: 'At night',
      value: 'night',
    },
    {
      id: 'care-recipient-needs-help-during-both',
      label: 'Day and night',
      value: 'both',
    },
  ],
  apply: (input, v) => {
    input.needsHelp = optionValue(NEEDS_HELP, v as string) as NeedsHelp;
  },
};

const IS_DISABLED: Question = {
  id: 'care-recipient-is-disabled',
  subject: 'care-recipient',
  prompt: 'Does {name} have a disability or long-term condition?',
  type: 'boolean',
  unlocks: 'PIP / ADP, Disabled Facilities Grant, Blue Badge',
  apply: (input, v) => {
    input.isDisabled = v as boolean;
  },
};

// How long, as two questions. It was one with four answers that differed only
// at the end of a long line, and a combined answer is harder to fill in from
// what somebody said: one discrete thing per question is what an LLM can pick
// out of a conversation. Most people this age answer yes to the first and never
// see the second.
const CONDITION_DURATION: Question = {
  id: 'care-recipient-condition-duration',
  subject: 'care-recipient',
  prompt: 'Has {name} had it for six months or more?',
  help: 'A terminal illness counts automatically.',
  type: 'choice',
  when: (input) => input.isDisabled === true,
  gate: 'if they have a disability',
  unlocks: 'Attendance Allowance, PIP / Adult Disability Payment',
  options: [
    { id: 'duration-6-months', label: 'Yes', value: 'yes' },
    { id: 'duration-under-6-months', label: 'No', value: 'no' },
    { id: 'duration-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const value = optionValue(CONDITION_DURATION, v as string);
    // No is "neither" until the next question says otherwise. Not sure leaves
    // it unanswered, so the rules that read it come back as more info needed.
    if (value === 'yes') input.conditionDuration = '6-months';
    if (value === 'no') input.conditionDuration = 'neither';
    // A quiz simplification: pick one representative disability so the bus-pass
    // and terminal-illness rules have something to read. Terminal is handled by
    // its own answer below; here we seed a generic long-term condition.
    if (input.disabilityTypes === undefined) input.disabilityTypes = ['other'];
  },
};

const CONDITION_EXPECTED: Question = {
  id: 'care-recipient-condition-expected',
  subject: 'care-recipient',
  prompt: 'Is it likely to last a year or more?',
  type: 'choice',
  when: (input) => input.conditionDuration === 'neither',
  gate: 'if they have had it less than six months',
  unlocks: 'PIP / Adult Disability Payment',
  options: [
    { id: 'expected-12-months', label: 'Yes', value: 'yes' },
    { id: 'expected-under-12-months', label: 'No', value: 'no' },
    { id: 'expected-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const value = optionValue(CONDITION_EXPECTED, v as string);
    if (value === 'yes') input.conditionDuration = '12-months-expected';
    if (value === 'not-sure') input.conditionDuration = undefined;
  },
};

// Bus pass disability types. Only asked under the bus pass age: over it, age
// alone qualifies (state pension age in England, 60 in Wales). A yes to the
// disability question is not enough on its own; the bus pass rules each read
// one named type. Scotland and NI have no disability route in the engine yet,
// so they are not asked.
const BUS_PASS_AGE: Partial<Record<Nation, number>> = {
  england: 66,
  wales: 60,
};

const DISABILITY_TYPES: Question = {
  id: 'care-recipient-disability-type',
  subject: 'care-recipient',
  prompt: 'Do any of these apply to {name}?',
  type: 'multi-select',
  when: (input) => {
    const age = input.nation ? BUS_PASS_AGE[input.nation] : undefined;
    return (
      input.isDisabled === true &&
      age !== undefined &&
      input.ageYears !== undefined &&
      input.ageYears < age
    );
  },
  gate: 'if disabled and under the bus pass age, in England or Wales',
  unlocks: 'Bus pass (disability route)',
  options: [
    {
      id: 'disability-blind',
      label: 'Blind or partially sighted',
      value: 'blind-or-partially-sighted',
    },
    { id: 'disability-deaf', label: 'Deaf', value: 'deaf' },
    {
      id: 'disability-without-speech',
      label: 'Cannot speak',
      value: 'without-speech',
    },
    {
      id: 'disability-learning',
      label: 'A learning disability',
      value: 'learning-disability',
    },
    {
      id: 'disability-walking',
      label: 'Cannot walk, or finds walking very hard',
      value: 'walking-disability',
    },
    {
      id: 'disability-arms',
      label: 'Can’t use either arm',
      value: 'no-arms-or-loss-of-use',
    },
    { id: 'disability-none', label: 'None of these', value: 'none' },
  ],
  apply: (input, v) => {
    const picked = (v as string[])
      .map((id) => optionValue(DISABILITY_TYPES, id))
      .filter((val) => val !== 'none') as DisabilityType[];
    // Asked after terminal illness, so a yes there has to survive this.
    const terminal = input.disabilityTypes?.includes('terminally-ill')
      ? (['terminally-ill'] as DisabilityType[])
      : [];
    input.disabilityTypes = [
      ...(picked.length > 0 ? picked : ['other' as const]),
      ...terminal,
    ];
  },
};

// Asked of everyone, not only inside the disability block. It is a route into
// Attendance Allowance at any age, and the warm path never asks the disability
// question, so gating it there lost it.
const TERMINAL: Question = {
  id: 'care-recipient-terminally-ill',
  subject: 'care-recipient',
  prompt: 'Does {name} have a terminal illness?',
  type: 'boolean',
  unlocks: 'Fast-tracks Attendance Allowance and PIP / ADP',
  apply: (input, v) => {
    const types = new Set<DisabilityType>(input.disabilityTypes ?? []);
    if (v === true) types.add('terminally-ill');
    else types.delete('terminally-ill');
    input.disabilityTypes = Array.from(types);
  },
};

const HOME_ADAPTATIONS: Question = {
  id: 'care-recipient-home-requires-adaptations',
  subject: 'care-recipient',
  prompt: "Does {name}'s home need adapting — ramps, a wet room, rails?",
  type: 'boolean',
  when: (input) => input.isDisabled === true,
  gate: 'if they have a disability',
  unlocks: 'Disabled Facilities Grant',
  apply: (input, v) => {
    input.homeRequiresAdaptations = v as boolean;
  },
};

const IN_NURSING_HOME: Question = {
  id: 'care-recipient-in-nursing-home',
  subject: 'care-recipient',
  prompt: 'Is {name} living in a care or nursing home?',
  type: 'boolean',
  when: (input) =>
    input.nation === 'england' ||
    input.nation === 'wales' ||
    input.nation === 'northern-ireland',
  gate: 'if in England, Wales or NI',
  unlocks: 'NHS-funded Nursing Care',
  apply: (input, v) => {
    input.inNursingHome = v as boolean;
  },
};

const NEEDS_PERSONAL_CARE: Question = {
  id: 'care-recipient-needs-personal-care',
  subject: 'care-recipient',
  prompt: 'Does {name} need help washing, dressing or eating?',
  type: 'boolean',
  when: (input) => input.nation === 'scotland',
  gate: 'if in Scotland',
  unlocks: 'Free Personal Care (Scotland)',
  apply: (input, v) => {
    input.needsPersonalCare = v as boolean;
  },
};

const NEEDS_NURSING_CARE: Question = {
  id: 'care-recipient-needs-nursing-care',
  subject: 'care-recipient',
  prompt: 'Does {name} need nursing care?',
  type: 'boolean',
  when: (input) => input.nation === 'scotland',
  gate: 'if in Scotland',
  unlocks: 'Free Nursing Care (Scotland)',
  apply: (input, v) => {
    input.needsNursingCare = v as boolean;
  },
};

// What they already get, by benefit. The rate or part only matters for a few
// of them, so it is asked after, and only of somebody who has that benefit:
// most people this age have Attendance Allowance and nothing else, and never
// see a follow-up. It used to be one list of nine with the parts spelled out,
// which ran off the screen.
const EXISTING_AWARDS: Question = {
  id: 'proto-existing-awards',
  subject: 'care-recipient',
  prompt: 'Does {name} already get any of these?',
  help: 'Production never asks this — it’s the gap that hides Carer’s Allowance and Blue Badge.',
  type: 'multi-select',
  unlocks: 'Unblocks Carer’s Allowance and Blue Badge',
  options: [
    {
      id: 'award-attendance-allowance',
      label: 'Attendance Allowance',
      value: 'attendance-allowance',
    },
    { id: 'award-pip', label: 'PIP', value: 'pip' },
    { id: 'award-dla', label: 'DLA', value: 'dla' },
    // The Scottish replacements for PIP and Attendance Allowance. Both qualify
    // a carer for Carer's Allowance; ADP only on its daily living part.
    {
      id: 'award-adp',
      label: 'Adult Disability Payment',
      value: 'adp',
      nations: ['scotland'],
    },
    {
      id: 'award-pension-age-disability-payment',
      label: 'Pension Age Disability Payment',
      value: 'pension-age-disability-payment',
      nations: ['scotland'],
    },
    { id: 'award-none', label: 'None of these', value: 'none' },
    { id: 'award-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const values = (v as string[]).map((id) =>
      optionValue(EXISTING_AWARDS, id),
    );
    // Not sure on its own is not "none": it leaves the awards unanswered.
    if (values.length > 0 && values.every((val) => val === 'not-sure')) return;
    input.existingAwards = values.filter(
      (val): val is ExistingAward =>
        val === 'attendance-allowance' ||
        val === 'pension-age-disability-payment',
    );
    input.awardsWithParts = values.filter(
      (val): val is AwardWithParts =>
        val === 'pip' || val === 'dla' || val === 'adp',
    );
  },
};

// Adds what a follow-up answered to the awards already held. Not sure adds
// nothing, so the part reads as not held.
const addAwards = (input: ScreenerInput, awards: ExistingAward[]) => {
  input.existingAwards = [...(input.existingAwards ?? []), ...awards];
};

const PIP_PARTS: Question = {
  id: 'proto-pip-parts',
  subject: 'care-recipient',
  prompt: 'Which part of PIP does {name} get?',
  type: 'choice',
  when: (input) => input.awardsWithParts?.includes('pip') === true,
  gate: 'if they get PIP',
  unlocks: 'Carer’s Allowance (daily living), Blue Badge (mobility)',
  options: [
    { id: 'pip-daily-living', label: 'Daily living', value: 'daily-living' },
    { id: 'pip-mobility', label: 'Mobility', value: 'mobility' },
    { id: 'pip-both', label: 'Both', value: 'both' },
    { id: 'pip-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const part = optionValue(PIP_PARTS, v as string);
    addAwards(input, [
      ...(part === 'daily-living' || part === 'both'
        ? (['pip-daily-living'] as const)
        : []),
      ...(part === 'mobility' || part === 'both'
        ? (['pip-mobility'] as const)
        : []),
    ]);
  },
};

const DLA_PARTS: Question = {
  id: 'proto-dla-parts',
  subject: 'care-recipient',
  prompt: 'Which parts of DLA does {name} get?',
  type: 'choice',
  when: (input) => input.awardsWithParts?.includes('dla') === true,
  gate: 'if they get DLA',
  unlocks: 'Carer’s Allowance (care), Blue Badge (mobility)',
  options: [
    {
      id: 'dla-care',
      label: 'Care, at the middle or highest rate',
      value: 'care',
    },
    {
      id: 'dla-mobility',
      label: 'Mobility, at the higher rate',
      value: 'mobility',
    },
    { id: 'dla-both', label: 'Both', value: 'both' },
    { id: 'dla-neither', label: 'Neither of those', value: 'neither' },
    { id: 'dla-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const part = optionValue(DLA_PARTS, v as string);
    addAwards(input, [
      ...(part === 'care' || part === 'both'
        ? (['dla-middle-or-high-care'] as const)
        : []),
      ...(part === 'mobility' || part === 'both'
        ? (['dla-higher-rate-mobility'] as const)
        : []),
    ]);
  },
};

// ADP mobility is asked so the question reads like PIP's, but nothing reads it
// yet: the Scottish Blue Badge route is not in the engine.
const ADP_PARTS: Question = {
  id: 'proto-adp-parts',
  subject: 'care-recipient',
  prompt: 'Which part of Adult Disability Payment does {name} get?',
  type: 'choice',
  when: (input) => input.awardsWithParts?.includes('adp') === true,
  gate: 'if they get Adult Disability Payment',
  unlocks: 'Carer Support Payment (daily living)',
  options: [
    { id: 'adp-daily-living', label: 'Daily living', value: 'daily-living' },
    { id: 'adp-mobility', label: 'Mobility', value: 'mobility' },
    { id: 'adp-both', label: 'Both', value: 'both' },
    { id: 'adp-not-sure', label: 'Not sure', value: 'not-sure' },
  ],
  apply: (input, v) => {
    const part = optionValue(ADP_PARTS, v as string);
    addAwards(
      input,
      part === 'daily-living' || part === 'both'
        ? ['adult-disability-payment']
        : [],
    );
  },
};

const CARER_PRESENT: Question = {
  id: 'proto-carer-present',
  subject: 'carer',
  prompt: 'Are you (or someone) caring for {name} unpaid?',
  type: 'boolean',
  unlocks: 'Carer’s Allowance',
  apply: (input, v) => {
    input.carerPresent = v as boolean;
    if (v === true) {
      // Quiz defaults — the carer is assumed 16+ and not in full-time
      // education (the common case). Both are confirmed precisely in the form.
      input.carerAgeYears = 40;
      input.carerFullTimeEducation = false;
    }
  },
};

const CARER_HOURS: Question = {
  id: 'proto-carer-hours-35-plus',
  subject: 'carer',
  prompt: 'Is that 35 hours a week or more?',
  type: 'boolean',
  when: (input) => input.carerPresent === true,
  gate: 'if a carer is present',
  unlocks: 'Carer’s Allowance threshold',
  apply: (input, v) => {
    input.carerHours35Plus = v as boolean;
  },
};

const CARER_EARNINGS: Question = {
  id: 'proto-carer-earnings-below-204',
  subject: 'carer',
  prompt: 'Does the carer earn under £204 a week?',
  type: 'boolean',
  when: (input) => input.carerPresent === true,
  gate: 'if a carer is present',
  unlocks: 'Carer’s Allowance earnings limit',
  apply: (input, v) => {
    input.carerEarningsBelow204 = v as boolean;
  },
};

const HAS_PARTNER: Question = {
  id: 'care-recipient-has-partner',
  subject: 'care-recipient',
  prompt: 'Does {name} have a partner or spouse?',
  type: 'boolean',
  unlocks: 'Pension Credit and Council Tax thresholds',
  apply: (input, v) => {
    input.hasPartner = v as boolean;
  },
};

const PARTNER_OVER_66: Question = {
  id: 'care-recipient-partner-date-of-birth',
  subject: 'partner',
  prompt: 'Is their partner also 66 or over?',
  type: 'boolean',
  when: (input) => input.hasPartner === true && (input.ageYears ?? 0) >= 66,
  gate: 'if they have a partner and are 66+',
  unlocks: 'Pension Credit (couple rate)',
  apply: (input, v) => {
    input.partnerAgeYears = v === true ? 70 : 60;
  },
};

const SAVINGS: Question = {
  id: 'care-recipient-savings-held-in-bank-accounts',
  subject: 'care-recipient',
  prompt: 'Roughly, how much does {name} have in savings?',
  type: 'choice',
  unlocks: 'Council Tax, Universal Credit, social care',
  options: [
    { id: 'savings-below-16k', label: 'Under £16,000', value: '10000' },
    { id: 'savings-16k-23250', label: '£16,000 to £23,250', value: '20000' },
    { id: 'savings-over-23250', label: 'Over £23,250', value: '30000' },
  ],
  apply: (input, v) => {
    input.savings = Number(optionValue(SAVINGS, v as string));
  },
};

const JOINT_INCOME: Question = {
  id: 'care-recipient-joint-weekly-income',
  subject: 'household',
  prompt: 'Roughly, their combined weekly income?',
  type: 'choice',
  when: (input) => input.hasPartner === true,
  gate: 'if they have a partner',
  unlocks: 'Pension Credit, Council Tax (couple)',
  options: [
    { id: 'joint-below-364', label: 'Under £364', value: '300' },
    { id: 'joint-364-450', label: '£364 to £450', value: '400' },
    { id: 'joint-over-450', label: 'Over £450', value: '500' },
  ],
  apply: (input, v) => {
    input.jointWeeklyIncome = Number(optionValue(JOINT_INCOME, v as string));
  },
};

const WEEKLY_INCOME: Question = {
  id: 'care-recipient-weekly-income',
  subject: 'care-recipient',
  prompt: 'Roughly, {name}’s weekly income?',
  type: 'choice',
  when: (input) => input.hasPartner === false,
  gate: 'if single',
  unlocks: 'Pension Credit, Council Tax (single)',
  options: [
    { id: 'income-below-238', label: 'Under £238', value: '200' },
    { id: 'income-238-300', label: '£238 to £300', value: '270' },
    { id: 'income-over-300', label: 'Over £300', value: '320' },
  ],
  apply: (input, v) => {
    input.weeklyIncome = Number(optionValue(WEEKLY_INCOME, v as string));
  },
};

export const QUESTIONS: Record<string, Question> = {
  [LOCATION.id]: LOCATION,
  [AGE_BAND.id]: AGE_BAND,
  [NEEDS_HELP.id]: NEEDS_HELP,
  [IS_DISABLED.id]: IS_DISABLED,
  [CONDITION_DURATION.id]: CONDITION_DURATION,
  [CONDITION_EXPECTED.id]: CONDITION_EXPECTED,
  [DISABILITY_TYPES.id]: DISABILITY_TYPES,
  [TERMINAL.id]: TERMINAL,
  [HOME_ADAPTATIONS.id]: HOME_ADAPTATIONS,
  [IN_NURSING_HOME.id]: IN_NURSING_HOME,
  [NEEDS_PERSONAL_CARE.id]: NEEDS_PERSONAL_CARE,
  [NEEDS_NURSING_CARE.id]: NEEDS_NURSING_CARE,
  [EXISTING_AWARDS.id]: EXISTING_AWARDS,
  [PIP_PARTS.id]: PIP_PARTS,
  [DLA_PARTS.id]: DLA_PARTS,
  [ADP_PARTS.id]: ADP_PARTS,
  [CARER_PRESENT.id]: CARER_PRESENT,
  [CARER_HOURS.id]: CARER_HOURS,
  [CARER_EARNINGS.id]: CARER_EARNINGS,
  [HAS_PARTNER.id]: HAS_PARTNER,
  [PARTNER_OVER_66.id]: PARTNER_OVER_66,
  [SAVINGS.id]: SAVINGS,
  [JOINT_INCOME.id]: JOINT_INCOME,
  [WEEKLY_INCOME.id]: WEEKLY_INCOME,
};

// The flow: nation, age, help first (so Attendance Allowance reveals fast), then
// care setting, what they already get, the carer and the money. The condition
// questions come last as a group: they are the ones people can talk through,
// and grouping them keeps the long ones out of the run of quick taps.
// Conditionals prune it to the minimum for each person.
export const QUIZ_ORDER: string[] = [
  LOCATION.id,
  AGE_BAND.id,
  NEEDS_HELP.id,
  IN_NURSING_HOME.id,
  NEEDS_PERSONAL_CARE.id,
  NEEDS_NURSING_CARE.id,
  EXISTING_AWARDS.id,
  PIP_PARTS.id,
  DLA_PARTS.id,
  ADP_PARTS.id,
  CARER_PRESENT.id,
  CARER_HOURS.id,
  CARER_EARNINGS.id,
  HAS_PARTNER.id,
  PARTNER_OVER_66.id,
  SAVINGS.id,
  JOINT_INCOME.id,
  WEEKLY_INCOME.id,
  IS_DISABLED.id,
  CONDITION_DURATION.id,
  CONDITION_EXPECTED.id,
  TERMINAL.id,
  HOME_ADAPTATIONS.id,
  // Last: nothing forks on it, and it decides only the bus pass.
  DISABILITY_TYPES.id,
];

// The first reveal fires the moment nation + age + help are in (question three).
export const REVEAL_AFTER_ID = NEEDS_HELP.id;

// Build a ScreenerInput by folding answered questions in order, skipping any
// whose `when` no longer holds (so stale answers don't leak).
export const buildInput = (
  answers: Answers,
  order: string[],
): ScreenerInput => {
  const input: ScreenerInput = {};
  for (const id of order) {
    const q = QUESTIONS[id];
    if (!q) continue;
    if (q.when && !q.when(input)) continue;
    const value = answers[id];
    if (value === undefined) continue;
    q.apply(input, value);
  }
  return input;
};

// The visible question sequence given current answers (conditionals resolved).
export const visibleQuestions = (
  answers: Answers,
  order: string[],
): Question[] => {
  const input: ScreenerInput = {};
  const visible: Question[] = [];
  for (const id of order) {
    const q = QUESTIONS[id];
    if (!q) continue;
    if (q.when && !q.when(input)) continue;
    visible.push(q);
    const value = answers[id];
    if (value !== undefined) q.apply(input, value);
  }
  return visible;
};
